import { Capacitor } from '@capacitor/core';

/**
 * API base URL — when running inside Capacitor natively, API calls need
 * absolute URLs because there's no Firebase Hosting rewriting.
 * On the web, relative URLs like '/create-checkout-session' are rewritten
 * by Firebase Hosting to your Cloud Run backend.
 */
const API_BASE = Capacitor.isNativePlatform()
    ? 'https://parentscheck.com'
    : '';

/**
 * Returns the full API URL for a given path.
 * @param path — e.g. '/create-checkout-session'
 * @returns — absolute URL in native, relative URL in web
 */
export function apiUrl(path: string): string {
    return `${API_BASE}${path}`;
}

export class ApiError extends Error {
    status: number;
    code: string;
    retryAfterSec?: number;

    constructor(status: number, code: string, message: string, retryAfterSec?: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.retryAfterSec = retryAfterSec;
    }
}

interface ApiErrorBody {
    error?: string;
    message?: string;
    retryAfterSec?: number;
    [key: string]: unknown;
}

export async function apiPost<T = Record<string, unknown>>(
    user: { getIdToken: () => Promise<string> },
    path: string,
    body: Record<string, unknown> = {},
): Promise<T> {
    const idToken = await user.getIdToken();

    let response: Response;
    try {
        response = await fetch(apiUrl(path), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify(body),
        });
    } catch {
        throw new ApiError(0, 'network_error', 'Network request failed.');
    }

    let payload: ApiErrorBody = {};
    try {
        payload = await response.json();
    } catch {
        payload = {};
    }

    if (!response.ok) {
        const code = typeof payload.error === 'string' ? payload.error : 'request_failed';
        const message = typeof payload.message === 'string' ? payload.message : code;
        const retryAfterSec = typeof payload.retryAfterSec === 'number' ? payload.retryAfterSec : undefined;
        throw new ApiError(response.status, code, message, retryAfterSec);
    }

    return payload as T;
}
